import type { CSSProperties } from 'react';
import {
  FlowchartEdgeKind,
  FlowchartMarkerKind,
  FlowchartNodeRole,
  FlowchartNodeShape,
} from './parse-mermaid-flowchart';
import { TRANSITION_DURATION } from './flowchart-constants';
import { DEPTH_COLORS } from '../Cmarkdownmindmap/mindmap-constants';

const EDGE_COLOR_GREEN = DEPTH_COLORS[0];
const EDGE_COLOR_BLUE = DEPTH_COLORS[1];
const EDGE_COLOR_NEUTRAL = '#8B9BB4';

enum EdgeRole {
  Entry = 'entry',
  Branch = 'branch',
  Default = 'default',
}

const COLOR_BY_ROLE: Record<EdgeRole, string> = {
  [EdgeRole.Entry]: EDGE_COLOR_GREEN,
  [EdgeRole.Branch]: EDGE_COLOR_BLUE,
  [EdgeRole.Default]: EDGE_COLOR_NEUTRAL,
};

const DASHED_BY_ROLE: Record<EdgeRole, boolean> = {
  [EdgeRole.Entry]: true,
  [EdgeRole.Branch]: false,
  [EdgeRole.Default]: false,
};

enum FlowDirection {
  Forward = 'forward',
  Reverse = 'reverse',
}

const HAIRLINE_STROKE_WIDTH = 1.6;
const THICK_STROKE_WIDTH = 2.8;
const ENTRY_DASH = '5 5';
const ACTIVE_STROKE_OPACITY = 0.85;
const DIMMED_STROKE_OPACITY = 0.32;
const ACTIVE_LABEL_OPACITY = 1;
const DIMMED_LABEL_OPACITY = 0.45;
const FOCUS_FLOW_DASH = '6 6';
const FOCUS_FLOW_ANIMATION_DURATION = '0.9s';
const FOCUS_FLOW_ANIMATION_FORWARD = 'fc-flow-fwd';
const FOCUS_FLOW_ANIMATION_REVERSE = 'fc-flow-rev';

interface EdgeRoleInput {
  isBack: boolean;
  kind: FlowchartEdgeKind;
  sourceRole: FlowchartNodeRole;
  sourceShape: FlowchartNodeShape;
}

export interface FlowchartEdgeActivityInput {
  focusedNodeId: string | null;
  focusedNodeIds: ReadonlySet<string>;
  hoveredConnectedNodeIds: ReadonlySet<string>;
  hoveredNodeId: string | null;
  source: string;
  target: string;
}

export interface FlowchartEdgeVisualState {
  dashArray: string | undefined;
  edgeColor: string;
  labelOpacity: number;
  markerEnd: string | undefined;
  markerStart: string | undefined;
  pathStyle: CSSProperties;
  strokeOpacity: number;
  strokeWidth: number;
}

export function resolveEdgeRole(input: EdgeRoleInput): EdgeRole {
  if (
    input.isBack ||
    input.kind === FlowchartEdgeKind.Dotted ||
    input.sourceRole === FlowchartNodeRole.Start
  ) {
    return EdgeRole.Entry;
  }

  return input.sourceShape === FlowchartNodeShape.Decision
    ? EdgeRole.Branch
    : EdgeRole.Default;
}

function resolveFlowDirection(input: {
  endMarker: FlowchartMarkerKind;
  focusedNodeId: string | null;
  source: string;
  startMarker: FlowchartMarkerKind;
}): FlowDirection {
  const arrowAtTarget = input.endMarker === FlowchartMarkerKind.Arrow;
  const arrowAtSource = input.startMarker === FlowchartMarkerKind.Arrow;

  if (arrowAtTarget && !arrowAtSource) {
    return FlowDirection.Forward;
  }

  if (arrowAtSource && !arrowAtTarget) {
    return FlowDirection.Reverse;
  }

  return input.source === input.focusedNodeId
    ? FlowDirection.Forward
    : FlowDirection.Reverse;
}

function resolveIsActive(input: FlowchartEdgeActivityInput): boolean {
  if (input.hoveredNodeId != null) {
    return input.hoveredConnectedNodeIds.has(input.source) ||
      input.hoveredConnectedNodeIds.has(input.target);
  }

  if (input.focusedNodeId != null) {
    return input.focusedNodeIds.has(input.source) ||
      input.focusedNodeIds.has(input.target);
  }

  return true;
}

function isFocusedEdge(activity: FlowchartEdgeActivityInput): boolean {
  if (activity.focusedNodeId == null) {
    return false;
  }

  return activity.source === activity.focusedNodeId || activity.target === activity.focusedNodeId;
}

function buildPathStyle(input: {
  focusAnimationName: string;
  isFocusEdge: boolean;
}): CSSProperties {
  const transition = `stroke-opacity ${TRANSITION_DURATION} ease-out`;

  return input.isFocusEdge
    ? {
        animation: `${input.focusAnimationName} ${FOCUS_FLOW_ANIMATION_DURATION} linear infinite`,
        transition,
      }
    : { transition };
}

function getMarkerUrl(input: {
  id: string;
  kind: FlowchartMarkerKind;
}): string | undefined {
  return input.kind === FlowchartMarkerKind.None ? undefined : `url(#${input.id})`;
}

function getFocusAnimationName(flowDirection: FlowDirection): string {
  return flowDirection === FlowDirection.Forward
    ? FOCUS_FLOW_ANIMATION_FORWARD
    : FOCUS_FLOW_ANIMATION_REVERSE;
}

function getDashArray(input: {
  baseDash: string | undefined;
  isFocusEdge: boolean;
}): string | undefined {
  return input.isFocusEdge ? FOCUS_FLOW_DASH : input.baseDash;
}

function getStrokeWidth(kind: FlowchartEdgeKind): number {
  return kind === FlowchartEdgeKind.Thick
    ? THICK_STROKE_WIDTH
    : HAIRLINE_STROKE_WIDTH;
}

export function resolveFlowchartEdgeVisualState(input: {
  activity: FlowchartEdgeActivityInput;
  edgeRole: EdgeRole;
  endMarker: FlowchartMarkerKind;
  endMarkerId: string;
  kind: FlowchartEdgeKind;
  startMarker: FlowchartMarkerKind;
  startMarkerId: string;
}): FlowchartEdgeVisualState {
  const edgeColor = COLOR_BY_ROLE[input.edgeRole];
  const isActive = resolveIsActive(input.activity);
  const isFocusEdge = isFocusedEdge(input.activity);
  const flowDirection = resolveFlowDirection({
    startMarker: input.startMarker,
    endMarker: input.endMarker,
    source: input.activity.source,
    focusedNodeId: input.activity.focusedNodeId,
  });
  const baseDash = DASHED_BY_ROLE[input.edgeRole] ? ENTRY_DASH : undefined;

  return {
    dashArray: getDashArray({ baseDash, isFocusEdge }),
    edgeColor,
    labelOpacity: isActive ? ACTIVE_LABEL_OPACITY : DIMMED_LABEL_OPACITY,
    markerEnd: getMarkerUrl({ id: input.endMarkerId, kind: input.endMarker }),
    markerStart: getMarkerUrl({ id: input.startMarkerId, kind: input.startMarker }),
    pathStyle: buildPathStyle({
      focusAnimationName: getFocusAnimationName(flowDirection),
      isFocusEdge,
    }),
    strokeOpacity: isActive ? ACTIVE_STROKE_OPACITY : DIMMED_STROKE_OPACITY,
    strokeWidth: getStrokeWidth(input.kind),
  };
}
